import { Box, Button, Flex, FormControl, Heading, HStack, Text, useToast } from "@chakra-ui/react";
import { AppDropdownSelect } from "components/AppDropdownSelect";

import AppInput from "components/AppInput";
import AppSelect from "components/AppSelect";
import { useFormik } from "formik";
import { useApi } from "hooks/useApi";
import { useCache } from "hooks/useCache";
import { useContext, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Store } from "store";
import { showToast, stringifyErrors, toastTypes } from "utils";
import * as Yup from "yup";

export default function ManageSubAreaPage() {
  const { id } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const { dispatch,state: { user } } = useContext(Store);
  const [institution, setInstitution] = useState("")
  const { getSubArea, addSubArea, updateSubArea, getAreas, getInstitutions } = useApi();
  const {
    data: subArea,
    isLoading,
    mutate,
  } = useCache("area", () => getSubArea(+id || 0));

  const {
    data: areas,
  } = useCache("areas", () => getAreas(1,"",values.area));

  const {
    data: institutions,
    isLoading: isInstitutionLoading,
    mutate: mutateInstitution,
  } = useCache("institutions", () => getInstitutions(1, institution, ""));


  const initialValues = useMemo(() => {
    return {
      name: subArea?.name || "",
      area: subArea?.area_id || "",
      institution: subArea?.institution_id || user?.user?.institution_id || "",
      submit: null,
    };
  }, [subArea, user]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required("Nom requis"),
    area: Yup.string().trim().required("Commune requis"),
    institution: Yup.string().trim().required("Institution requis"),
  });

  const {
    errors,
    values,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const postData: any = {
          name: values.name,
          area_id: values.area,
          institution_id: values.institution,
        };
        let response: any = null;
        if (id) {
          response = await updateSubArea({
            id: id,
            ...postData,
          });
        } else {
          response = await addSubArea({
            ...postData,
          });
        }
        if (!response.error_code && !response.errors) {
          dispatch(
            showToast({
              toast: toast,
              message: id
                ? "Zone mis à jour avec succès"
                : "Zone créé avec succès",
              status: toastTypes.success,
            })
          );
          if (id) {
            mutate(getSubArea(+id));
            navigate("/sub-areas");
          } else {
            navigate(`/sub-areas`);
          }
        } else {
          dispatch(
            showToast({
              toast: toast,
              message: response.errors
                ? stringifyErrors(response.errors)
                : response.message,
              status: toastTypes.error,
            })
          );
        }
      } catch (ex) {
        dispatch(
          showToast({
            toast: toast,
            message: "Quelque chose s'est mal passé",
            status: toastTypes.error,
          })
        );
      }
    },
  });
  return (
    <>
      {!isLoading && (
        <Flex
          gap="18px"
          bg="very_light_gray"
          flex={1}
          flexDirection="column"
          py="20px"
          px="30px"
          minH={"100vh"}
        >
          <Box>
            <Flex gap="4px" flexDirection="column">
              <Flex justifyContent="center" alignItems="center">
                <Heading size="text2xl" as="h4" fontFamily="Arimo">
                  {id ? "Modifier une zone" : "Créer une zone"}
                </Heading>
                <Flex
                  pl="56px"
                  pr="62px"
                  gap="9px"
                  flex={1}
                  justifyContent="flex-end"
                  px={{ md: 0, base: "20px" }}
                >
                </Flex>
              </Flex>
            </Flex>
          </Box>
          <form
            onSubmit={handleSubmit}
            style={{
              flexDirection: "column",
              margin: "0px",
              alignItems: "center",
            }}
          >
            <Flex flexDirection="column" alignItems="center">
              <Flex
                gap="16px"
                bg="white"
                boxShadow="xl"
                w="100%"
                flexDirection="column"
                px={{ base: "20px", sm: "24px" }}
                py={{ base: "20px", sm: "24px" }}
                mx={{ md: "62px", base: "0px" }}
                borderRadius="16px"
              >
                <Flex gap="16px" flexDirection="column">
                  <Flex gap="4px" flexDirection="column" alignItems="start">
                    <Text color="gray_description">Nom</Text>
                    <FormControl
                      isInvalid={!!errors.name && (touched.name as boolean)}
                    >
                      <AppInput
                        required
                        placeholder={`Nom`}
                        borderWidth="1px"
                        alignSelf="stretch"
                        borderRadius="8px"
                        id="name"
                        name="name"
                        autoComplete="name"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.name}
                        showErrorMessage={Boolean(touched.name && errors.name)}
                        errorMessage={errors.name}
                      />
                    </FormControl>
                  </Flex>
                  <Flex gap="4px" flexDirection="column" alignItems="start">
                  <Text color="gray_description">Commune</Text>
                  <Flex
                    gap="4px"
                    flexDirection="column"
                    alignItems="start"
                    width={"100%"}
                  >
                    <AppDropdownSelect
                      name="area"
                      data={areas}
                      value={values.area}
                      onChange={setFieldValue}
                      placeholder="Veuillez sélectionner une commune"
                    />
                  </Flex>
                </Flex>
                <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                      mt={1}
                    >
                      <HStack>
                        <Text color="gray_description">Institution</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={
                          !!errors.institution && (touched.institution as boolean)
                        }
                      >
                        {!isInstitutionLoading && !user?.user?.institution_id && (
                          <AppSelect
                            id="institution"
                            name="institution"
                            onChange={(field: string, value: any) => {
                              setFieldValue(field, value);
                              setInstitution(value);
                              mutateInstitution(getInstitutions(1, value, ""));
                            }}
                            value={values.institution || initialValues.institution}
                            placeholder="Veuillez sélectionner une institution"
                            options={institutions}
                            isMulti={false}
                            isLoading={isInstitutionLoading}
                            showErrorMessage={Boolean(touched.institution && errors.institution)}
                            errorMessage={errors.institution}
                            onBlur={handleBlur}
                          />
                        )}
                      </FormControl>
                    </Flex>
                </Flex>
              </Flex>
              <Flex mt="24px" gap="12px">
                <Button
                  size="4xl"
                  color="gray_description"
                  fontFamily="Arimo"
                  fontWeight={700}
                  minW="106px"
                  borderRadius="8px"
                  onClick={() => window.history.back()}
                >
                  Annuler
                </Button>
                <Button
                  size="4xl"
                  colorScheme="red_origin"
                  fontFamily="Arimo"
                  fontWeight={700}
                  minW="132px"
                  borderRadius="8px"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Enregistrer
                </Button>
              </Flex>
            </Flex>
          </form>
        </Flex>
      )}
    </>
  );
}
